import { yupResolver } from '@hookform/resolvers/yup';
import {
	Chip,
	CircularProgress,
	Collapse,
	FormControlLabel,
	Link,
	Radio,
	RadioGroup,
	Skeleton,
	Stack,
	SxProps,
	Theme,
	Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React from 'react';
import {
	Controller,
	ControllerRenderProps,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import * as yup from 'yup';
import { usePaginatedPayments } from '../../../api/PaymentsApi';
import { usePolicy } from '../../../api/PolicyApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import { CashBalanceResponse } from '../../../types/cashBalance';
import { CatchUpInvoice } from '../../../types/catchUps';
import { CurrencyCode } from '../../../types/currencyCode';
import { Payment } from '../../../types/payments';
import {
	FieldValuesMap,
	InvoicePaymentRequest,
	PaymentCreateRequest,
	PolicyInvoice2Response,
} from '../../../types/policy';
import { PremiumReportingInvoice } from '../../../types/premiumReports';
import { formatCurrency } from '../../../util/util';
import { FastForm } from '../../fastForm';
import {
	FORM_MESSAGE,
	FORM_NAME,
	PAYMENT_SPECIAL_FIELDS,
} from '../../fastForm/data/constants';
import { CurrencyField } from '../../fastForm/fields';
import {
	getDefaultPolicyValues,
	getPaymentForm,
} from '../../fastForm/formBuilders';
import paymentUpdateBuilder from '../../fastForm/updateBuilders/paymentUpdateBuilder';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import {
	PAY_INVOICE_MODAL__APPLY_NEW_PAYMENT,
	PAY_INVOICE_MODAL__CANCEL_BUTTON,
	PAY_INVOICE_MODAL__DONE_BUTTON,
	PAY_INVOICE_MODAL__INVOICE_LABEL,
	PAY_INVOICE_MODAL__NO_ELIGIBLE_PAYMENTS,
	PAY_INVOICE_MODAL__NO_PAYMENTS_FOUND,
	PAY_INVOICE_MODAL__PAYMENT_DISPLAY,
	PAY_INVOICE_MODAL__PAYMENT_DUE_DATE_LABEL,
	PAY_INVOICE_MODAL__PAYMENT_STATE,
	PAY_INVOICE_MODAL__PAY_NOW_BUTTON,
	PAY_INVOICE_MODAL__POLICY_LABEL,
	PAY_INVOICE_MODAL__RETAIN_IN_CASH_BALANCE,
	PAY_INVOICE_MODAL__SELECT_PAYMENT,
	PAY_INVOICE_MODAL__TITLE,
	PAY_INVOICE_MODAL__TOTAL_DUE_LABEL,
	PAY_INVOICE_MODAL__USE_CASH_BALANCE,
} from '../data/constants';

interface PayInvoiceModalProps {
	open: boolean;
	invoice: PolicyInvoice2Response | PremiumReportingInvoice | CatchUpInvoice;
	isCallbackLoading: boolean;
	isCallbackSuccess: boolean;
	callback: (
		policyId: string,
		locator: string,
		request: PaymentCreateRequest,
	) => Promise<Payment>;
	setOpen: (val: Boolean) => void;
	isCatchUp?: boolean;
}

export interface PayInvoiceModalForm {
	paymentLocator?: string;
	payment: {
		amount: string;
		[configuredFields: string]: string | boolean | number | string[] | number[];
	};
}

interface RadioContentProps {
	payment?: Payment;
	field: ControllerRenderProps<PayInvoiceModalForm, 'paymentLocator'>;
	cashBalance?: CashBalanceResponse;
	negativeInvoice: boolean;
}

const BORDER_STYLES: SxProps<Theme> = {
	border: 1,
	borderRadius: 2,
	borderColor: 'white.dark',
};

const PAYMENTS_PAGE_SIZE = 25;
const VISIBLE_PAYMENTS_PAGE_SIZE = 4;
const PAYMENTS_SCROLL_AREA_HEIGHT = 400;

function RadioContent({
	payment,
	field,
	cashBalance,
	negativeInvoice,
}: RadioContentProps): JSX.Element {
	const authData = useAuthContext();
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	return (
		<FormControlLabel
			key={payment?.displayId || cashBalance.locator}
			value={payment?.locator || cashBalance.locator}
			control={<Radio color='secondary' size='small' />}
			sx={{
				width: '100%',
				...BORDER_STYLES,
				borderColor:
					field.value === payment?.locator ||
					field.value === cashBalance?.locator
						? 'secondary.main'
						: 'white.dark',
				marginX: 0,
				marginBottom: 2,
				paddingY: 1,

				'&:last-child': {
					marginBottom: 0,
				},

				'& .MuiFormControlLabel': {
					'&-label': {
						width: '100%',
						paddingX: 1,
					},
				},
			}}
			label={
				<Stack
					direction='row'
					justifyContent={'space-between'}
					alignItems={'center'}
				>
					{!cashBalance ? (
						<Stack direction='column' width='100%' justifyContent={'center'}>
							<Stack direction='row'>
								<Typography color='black.darkest' marginRight={2}>
									{PAY_INVOICE_MODAL__PAYMENT_DISPLAY(payment.displayId)}
								</Typography>
								<Chip
									label={PAY_INVOICE_MODAL__PAYMENT_STATE}
									color='info'
									variant='outlined'
									size='small'
								/>
							</Stack>
						</Stack>
					) : (
						<Typography>
							{cashManagementEnabled && negativeInvoice
								? PAY_INVOICE_MODAL__RETAIN_IN_CASH_BALANCE
								: PAY_INVOICE_MODAL__USE_CASH_BALANCE}
						</Typography>
					)}
					<Stack
						direction='column'
						sx={{
							width: !cashBalance ? '100%' : 'auto',
							textAlign: 'right',
						}}
					>
						{!cashBalance ? (
							<Typography variant='h3Medium' color='black.darkest'>
								{formatCurrency(
									parseFloat(payment.amount),
									payment.amountCurrency as CurrencyCode,
								)}
							</Typography>
						) : (
							<Chip
								sx={{ fontSize: '1rem' }}
								label={formatCurrency(
									parseFloat(cashBalance.amount),
									cashBalance.currency as CurrencyCode,
								)}
								color={parseFloat(cashBalance.amount) < 0 ? 'error' : 'success'}
								variant='outlined'
								size='medium'
							/>
						)}
					</Stack>
				</Stack>
			}
			labelPlacement='end'
		/>
	);
}

export default function PayInvoiceModal(props: PayInvoiceModalProps) {
	const authData = useAuthContext();
	const config = useConfigContext();
	const policyData = usePolicyContext();
	const { invoice, callback, open, setOpen, isCallbackLoading, isCatchUp } =
		props;
	const authToken = authData.auth?.authorizationToken;
	const timezone = authData.auth?.timezone;
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const hasPermission = authData?.hasPermission;
	const [page, setPage] = React.useState({
		page: 0,
		pageSize: PAYMENTS_PAGE_SIZE,
	});
	const [formWizard, setFormWizard] = React.useState<
		'initial' | 'applyPayment'
	>('initial');

	const newPaymentSchema = (invoiceAmount: string) =>
		yup.object().shape({
			payment: yup.object().shape({
				amount: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test('number', FORM_MESSAGE.number, (value) => {
						if (value === undefined || value === null) {
							return true;
						}
						return !isNaN(Number(value));
					})
					.test(
						'isPositiveAmountForPositiveInvoice',
						FORM_MESSAGE.postiveNum,
						(value) => {
							return parseFloat(invoiceAmount) > 0
								? parseFloat(value) > 0
								: true;
						},
					)
					.test(
						'isNegativeAmountForNegativeInvoice',
						FORM_MESSAGE.negativeNum,
						(value) => {
							return parseFloat(invoiceAmount) < 0
								? parseFloat(value) < 0
								: true;
						},
					),
			}),
		});

	const {
		data: paymentsResponse,
		isLoading: arePaymentsLoading,
		fetchNextPage,
		hasNextPage,
		isFetched: arePaymentsFetched,
		isFetchingNextPage: isFetchingPaymentNextPage,
	} = usePaginatedPayments(
		page.pageSize,
		authToken,
		null,
		invoice?.policyLocator,
		'ready',
	);
	const [payments, setPayments] = React.useState<Payment[]>([]);

	React.useEffect(() => {
		if (paymentsResponse?.pages?.length) {
			let newPaymentsState = paymentsResponse.pages
				.map((pageData) => pageData.data.payments)
				.flat();

			// ready payments have to match with policy's currency so no need to filter by currency
			if (parseFloat(invoice.totalDue) < 0) {
				// hide positive payments for negative invoice
				newPaymentsState = newPaymentsState.filter(
					(payment) => parseFloat(payment.amount) < 0,
				);
			} else if (parseFloat(invoice.totalDue) > 0) {
				// hide negative payments for positive invoice
				newPaymentsState = newPaymentsState.filter(
					(payment) => parseFloat(payment.amount) > 0,
				);
			}
			setPayments(newPaymentsState);
		}
	}, [page.page, paymentsResponse, invoice]);

	const { data: policy, isLoading: isPolicyLoading } = usePolicy(
		invoice.policyLocator,
		authToken,
	);
	const cashBalance =
		policy?.cashBalance &&
		policy.cashBalance.currency === invoice.totalDueCurrency &&
		policy.cashBalance;
	const canPayWithCashBalance =
		policy?.cashBalance?.amount &&
		parseFloat(policy?.cashBalance?.amount) > 0 &&
		parseFloat(invoice.totalDue) > 0;
	const canRetainInCashBalance = parseFloat(invoice.totalDue) < 0;

	const currency = invoice.totalDueCurrency;

	const defaultPaymentLocator = payments?.length
		? payments[0].locator
		: cashBalance?.locator || '';

	const form = getPaymentForm({
		fields: config.paymentConfiguration.paymentConfiguration.fields,
		fieldValues: {},
		mediaByLocator: {},
		timezone,
		multiCurrencyEnabled,
	});

	const defaultValues = getDefaultPolicyValues(form, authData);

	const methods = useForm<PayInvoiceModalForm>({
		defaultValues: {
			paymentLocator: '',
			payment: {
				...(defaultValues.payment as object),
				amount: invoice.totalDue,
			},
		},
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			formWizard === 'applyPayment' &&
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(
					newPaymentSchema(invoice.totalDue),
				),
			),
	});

	const {
		handleSubmit,
		formState,
		control,
		reset: resetForm,
		setValue,
	} = methods;
	const dirtyFields = formState.dirtyFields;

	React.useEffect(() => {
		setValue('paymentLocator', defaultPaymentLocator);
	}, [defaultPaymentLocator, setValue]);

	const handleClose = () => {
		resetForm();
		setOpen(false);
	};

	const handleGoBack = () => {
		resetForm();
		setValue('paymentLocator', defaultPaymentLocator);
		setFormWizard('initial');
	};

	const onSubmit: SubmitHandler<PayInvoiceModalForm> = async (formValues) => {
		let request: InvoicePaymentRequest = {};
		if (formValues.paymentLocator && formWizard === 'initial') {
			request =
				// pay an invoice using cash balance by passing payment of amount 0
				formValues.paymentLocator === cashBalance.locator
					? {
							payment: {
								amount: 0,
							},
					  }
					: { paymentLocator: formValues.paymentLocator };
		}
		if (formWizard === 'applyPayment') {
			const updateRequest = paymentUpdateBuilder(
				dirtyFields,
				formValues,
				form,
				FORM_NAME.payment,
				timezone,
				currency,
			);
			const amount = formValues.payment.amount;
			const fieldValues = updateRequest.fieldValues;
			request = {
				payment: {
					amount: parseFloat(amount),
					fieldValues: fieldValues as FieldValuesMap,
				},
			};
		}

		await callback(invoice.policyLocator, invoice.locator, request);
		handleClose();
	};

	if (!invoice) return null;

	const getDialogContent = () => {
		return (
			<FormProvider {...methods}>
				<Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle>{PAY_INVOICE_MODAL__TITLE(isCatchUp)}</DialogTitle>
					<DialogContent>
						<Collapse in={formWizard === 'initial'}>
							<Stack
								direction='row'
								justifyContent='space-between'
								paddingBottom={2}
							>
								<Stack direction='column' width='100%'>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__INVOICE_LABEL(isCatchUp)}
									</Typography>
									<Typography variant='bodyMedium'>
										{invoice.displayId}
									</Typography>
								</Stack>
								<Stack direction='column' width='100%'>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__POLICY_LABEL}
									</Typography>
									<Typography variant='bodyMedium'>
										{invoice.policyLocator}
									</Typography>
								</Stack>
							</Stack>
							<Stack
								direction='row'
								justifyContent='space-between'
								paddingBottom={2}
							>
								<Stack direction='column' width={'100%'}>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__PAYMENT_DUE_DATE_LABEL}
									</Typography>
									<Typography variant='bodyMedium'>
										{authData.getZonedFormattedDateString(invoice.dueTimestamp)}
									</Typography>
								</Stack>
								<Stack direction='column' width={'100%'}>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__TOTAL_DUE_LABEL}
									</Typography>
									<Typography variant='bodyMedium'>
										{formatCurrency(
											parseFloat(invoice.totalDue),
											invoice.totalDueCurrency,
										)}
									</Typography>
								</Stack>
							</Stack>
							<Stack
								direction='row'
								paddingBottom={2}
								spacing={1}
								alignItems='center'
							>
								<Typography
									variant='bodySemiBold'
									id='PayInvoiceModal__RadioButtonsGroupLabel'
								>
									{PAY_INVOICE_MODAL__SELECT_PAYMENT}
								</Typography>
								<Link
									component='button'
									type='button'
									onClick={() => setFormWizard('applyPayment')}
								>
									{PAY_INVOICE_MODAL__APPLY_NEW_PAYMENT}
								</Link>
							</Stack>
							{arePaymentsLoading || isPolicyLoading ? (
								<Skeleton
									sx={{
										...BORDER_STYLES,
									}}
									height={80}
								/>
							) : (
								<>
									{arePaymentsFetched && (cashBalance || payments?.length) ? (
										<Controller
											name='paymentLocator'
											control={control}
											render={({ field }) => (
												<RadioGroup {...field}>
													{canPayWithCashBalance || canRetainInCashBalance ? (
														<RadioContent
															field={field}
															cashBalance={cashBalance}
															negativeInvoice={parseFloat(invoice.totalDue) < 0}
														/>
													) : null}
													{payments?.length ? (
														<InfiniteScroll
															style={{
																overflowX: 'hidden',
																paddingRight: '5px',
															}}
															dataLength={payments.length}
															next={() => {
																setPage({
																	...page,
																	page: page.page + 1,
																});
																fetchNextPage();
															}}
															hasMore={hasNextPage}
															loader={
																isFetchingPaymentNextPage ? (
																	<CircularProgress size={15} />
																) : null
															}
															height={
																hasNextPage &&
																payments.length >= VISIBLE_PAYMENTS_PAGE_SIZE
																	? PAYMENTS_SCROLL_AREA_HEIGHT
																	: 'auto'
															}
														>
															{payments.map((payment) => (
																<RadioContent
																	key={payment.locator}
																	payment={payment}
																	field={field}
																	negativeInvoice={
																		parseFloat(invoice.totalDue) < 0
																	}
																/>
															))}
														</InfiniteScroll>
													) : (
														<Box
															sx={{
																...BORDER_STYLES,
																padding: 2,
																textAlign: 'center',
															}}
														>
															<Typography></Typography>
															<Typography color='black.opacity60'>
																{PAY_INVOICE_MODAL__NO_PAYMENTS_FOUND}
															</Typography>
														</Box>
													)}
												</RadioGroup>
											)}
										/>
									) : (
										<Box
											sx={{
												...BORDER_STYLES,
												padding: 2,
												textAlign: 'center',
											}}
										>
											{PAY_INVOICE_MODAL__NO_ELIGIBLE_PAYMENTS}
										</Box>
									)}
								</>
							)}
						</Collapse>
						<Collapse in={formWizard === 'applyPayment'}>
							<Stack
								direction='row'
								justifyContent='space-between'
								paddingBottom={2}
							>
								<Stack direction='column' width='100%'>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__INVOICE_LABEL(isCatchUp)}
									</Typography>
									<Typography variant='bodyMedium'>
										{invoice.displayId}
									</Typography>
								</Stack>
								<Stack direction='column' width='100%'>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__POLICY_LABEL}
									</Typography>
									<Typography variant='bodyMedium'>
										{invoice.policyLocator}
									</Typography>
								</Stack>
							</Stack>
							<Stack
								direction='row'
								justifyContent='space-between'
								paddingBottom={2}
							>
								<Stack direction='column' width={'100%'}>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__PAYMENT_DUE_DATE_LABEL}
									</Typography>
									<Typography variant='bodyMedium'>
										{authData.getZonedFormattedDateString(invoice.dueTimestamp)}
									</Typography>
								</Stack>
								<Stack direction='column' width={'100%'}>
									<Typography color='black.opacity60'>
										{PAY_INVOICE_MODAL__TOTAL_DUE_LABEL}
									</Typography>
									<Typography variant='bodyMedium'>
										{formatCurrency(
											parseFloat(invoice.totalDue),
											invoice.totalDueCurrency,
										)}
									</Typography>
								</Stack>
							</Stack>
							<Stack direction='column' spacing={2}>
								<CurrencyField
									order={0}
									name={`${FORM_NAME.payment as 'payment'}.${
										PAYMENT_SPECIAL_FIELDS.amount
									}`}
									title='Amount'
									currency={currency}
									disabled={isCallbackLoading}
									control={control}
									type='select'
									values={[currency]}
								/>
								<FastForm
									formName={FORM_NAME.payment}
									form={form}
									productName={policyData?.productConfig?.name}
									configVersion=''
									authData={authData}
									disabled={isCallbackLoading}
								/>
							</Stack>
						</Collapse>
					</DialogContent>

					<DialogActions
						sx={{
							marginRight: 2,
							marginY: 2,
							position: 'sticky',
							bottom: 0,
							backgroundColor: 'white.lightest',
						}}
					>
						{isPolicyLoading || arePaymentsLoading ? (
							<>
								<Skeleton width={70} height={50} />
								<Skeleton width={70} height={50} />
							</>
						) : (
							<>
								{(formWizard === 'initial' &&
									(canPayWithCashBalance ||
										canRetainInCashBalance ||
										payments?.length)) ||
								formWizard === 'applyPayment' ? (
									<>
										<Button
											onClick={() =>
												formWizard === 'initial'
													? handleClose()
													: handleGoBack()
											}
											color='neutralButton'
										>
											{PAY_INVOICE_MODAL__CANCEL_BUTTON}
										</Button>
										{hasPermission('invoice.pay') && (
											<Button
												type='submit'
												variant='contained'
												color='secondary'
												disabled={isCallbackLoading}
												startIcon={
													isCallbackLoading && <CircularProgress size={20} />
												}
											>
												{PAY_INVOICE_MODAL__PAY_NOW_BUTTON}
											</Button>
										)}
									</>
								) : (
									<Button
										type='submit'
										variant='contained'
										color='secondary'
										onClick={handleClose}
									>
										{PAY_INVOICE_MODAL__DONE_BUTTON}
									</Button>
								)}
							</>
						)}
					</DialogActions>
				</Box>
			</FormProvider>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} scroll='paper'>
			<Box
				sx={{
					width: {
						xs: '90%',
						sm: '90%',
						md: 600,
						lg: 600,
						xl: 600,
					},
					overflow: 'hidden',
				}}
			>
				{getDialogContent()}
			</Box>
		</Dialog>
	);
}
